import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { CONNECTION_TYPES, LOOKUP_META, statusChangeSchema } from '@app/shared';
import type { ConnectionType, CustomerStatus, StatusChangeInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { ConfirmDialog } from '../../components/dialogs';
import { SelectInput, applyServerErrors, toOptions } from '../../components/form';
import { ApiError, errorMessage } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { useLookupOptions } from '../configuration/api';
import { addFieldError, withRequiredMessages } from '../inventory/formUtils';
import { useCustomerContext } from './CustomerLayout';
import { RestorePayment } from './RestorePayment';
import { useChangeStatus } from './api';
import { Section, customerPath } from './parts';

/** "Dispatched to Active" has its own button, "Activate Customer", and is not in the select. */
const ACTIVATE_CODE = 'PC500';

interface StatusForm {
  status: CustomerStatus | '';
  connectionType: ConnectionType | '';
  reasonId: string | null;
}

/**
 * Update Account Status (PC95). The select offers the changes the server allows from the
 * current status for this user; the reason list appears when the chosen change needs one.
 */
export function StatusPage() {
  const { customer } = useCustomerContext();
  const navigate = useNavigate();
  const notify = useNotify();
  const changeStatus = useChangeStatus(customer.id);

  const allowed = customer.allowedStatusChanges;
  const choices = allowed.filter((t) => t.code !== ACTIVATE_CODE);
  const canActivate = allowed.some((t) => t.code === ACTIVATE_CODE);

  const resolver = useMemo<Resolver<StatusForm>>(() => {
    const base = withRequiredMessages<StatusForm>(
      zodResolver(statusChangeSchema) as unknown as Resolver<StatusForm>,
      { status: 'Select New Status', connectionType: 'Select Connection Type' },
    );
    return async (values, context, options) => {
      const result = await base(values, context, options);
      const needsReason = allowed.find((t) => t.to === values.status)?.reason;
      return needsReason && !values.reasonId
        ? addFieldError(result, 'reasonId', 'Select Reason')
        : result;
    };
  }, [allowed]);

  const { control, handleSubmit, getValues, setValue, setError, trigger, watch } =
    useForm<StatusForm>({
      defaultValues: { status: '', connectionType: '', reasonId: null },
      resolver,
    });
  const status = useWatch({ control, name: 'status' });
  const reasonKind = choices.find((t) => t.to === status)?.reason ?? null;
  const reasons = useLookupOptions(reasonKind ?? 'disconnectReason', Boolean(reasonKind));
  const planRanOut = Boolean(
    customer.line.planExpiresAt && new Date(customer.line.planExpiresAt).getTime() <= Date.now(),
  );
  const featureOn = useFeatureReleased();
  // Restoring a line whose plan ran out is a payment on this screen (release feature switch).
  const payOnRestore =
    status === 'Active' &&
    customer.status === 'Suspended' &&
    planRanOut &&
    featureOn('restore-payment');

  // Each change has its own reason list, so a reason picked for another change is dropped.
  // Done in the change itself (not after rendering), so the new list never shows the old reason.
  useEffect(() => {
    const subscription = watch((_values, { name }) => {
      if (name === 'status') setValue('reasonId', null);
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  /** The change waiting for confirmation. */
  const [pending, setPending] = useState<StatusChangeInput | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const askUpdate = handleSubmit((values) => {
    setFailure(null);
    setPending({
      status: values.status as CustomerStatus,
      connectionType: values.connectionType as ConnectionType,
      reasonId: reasonKind ? values.reasonId : null,
    });
  });

  const askActivate = async () => {
    setFailure(null);
    // Activation uses the connection type chosen above and nothing else of the form.
    if (!(await trigger('connectionType'))) return;
    setPending({
      status: 'Active',
      connectionType: getValues('connectionType') as ConnectionType,
      reasonId: null,
    });
  };

  const confirm = async () => {
    if (!pending) return;
    try {
      const updated = await changeStatus.mutateAsync(pending);
      notify.success(`Account status changed to ${updated.status}.`);
      navigate(customerPath(customer.id));
    } catch (err) {
      setPending(null);
      // A refusal by the carrier (502) or a conflict has no field, and a field error may be
      // about a field this form does not show right now; both are shown above the form.
      const shown = ['connectionType', choices.length > 0 && 'status', reasonKind && 'reasonId'];
      const onForm =
        err instanceof ApiError &&
        err.details.length > 0 &&
        err.details.every((d) => shown.includes(d.path));
      applyServerErrors(err, setError);
      if (!onForm) setFailure(errorMessage(err));
    }
  };

  if (choices.length === 0 && !canActivate)
    return (
      <Section title="Update Account Status">
        <Alert severity="info">
          No status change is available for an account that is {customer.status} with the
          permissions of your user group.
        </Alert>
      </Section>
    );

  return (
    <Section title="Update Account Status">
      {failure ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFailure(null)}>
          {failure}
        </Alert>
      ) : null}
      <Box
        component="form"
        noValidate
        onSubmit={(e) => void askUpdate(e)}
        sx={{ display: 'grid', gap: 2, maxWidth: 480 }}
      >
        {choices.length > 0 ? (
          <SelectInput
            control={control}
            name="status"
            label="Change Account Status To:"
            required
            options={choices.map((t) => ({ value: t.to, label: `${t.label} (${t.code})` }))}
          />
        ) : null}
        <SelectInput
          control={control}
          name="connectionType"
          label="Connection Type:"
          required
          options={toOptions(CONNECTION_TYPES)}
        />
        {payOnRestore ? <RestorePayment customer={customer} /> : null}
        {status === 'Active' && customer.status === 'Suspended' && planRanOut && !payOnRestore ? (
          <Alert severity="warning">
            The plan of this line ran out on {customer.line.planExpiresAt?.slice(0, 10)}. A restore
            takes the plan amount, tax and fees:{' '}
            <Link to={`${customerPath(customer.id)}/plan-renew`}>pay it on Plan Renew (PC519)</Link>
            , which brings the line back.
          </Alert>
        ) : null}
        {reasonKind ? (
          <SelectInput
            control={control}
            name="reasonId"
            label={LOOKUP_META[reasonKind].title}
            required
            options={(reasons.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
            helperText={reasons.isError ? 'Could not load the reasons' : undefined}
          />
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canActivate ? (
            <Button variant="outlined" onClick={() => void askActivate()}>
              Activate Customer ({ACTIVATE_CODE})
            </Button>
          ) : null}
          {choices.length > 0 && !payOnRestore ? (
            <Button type="submit" variant="contained">
              Update Account Status
            </Button>
          ) : null}
        </Box>
      </Box>

      <ConfirmDialog
        open={Boolean(pending)}
        title="Update Account Status"
        message={
          pending
            ? `Change this account from ${customer.status} to ${pending.status} (${pending.connectionType})?`
            : ''
        }
        confirmLabel={pending?.status === 'Active' && canActivate ? 'Activate Customer' : 'Update'}
        danger={pending?.status === 'Inactive'}
        busy={changeStatus.isPending}
        onConfirm={() => void confirm()}
        onClose={() => setPending(null)}
      />
    </Section>
  );
}
